import React, {
  ChangeEventHandler,
  FocusEventHandler,
  MouseEventHandler,
  forwardRef,
} from "react";
import {
  FormHelperText,
  InputBase,
  InputBaseComponentProps,
  InputLabel,
  Theme,
  makeStyles,
} from "@material-ui/core";
import classnames from "classnames";
import { LG, SM } from "../../types";
import { styles } from "./styles";
import { InputHelpText } from "..";

interface Props {
  id?: string;
  name?: string;
  className?: string;
  size?: SM | LG | "multi";
  label?: string;
  disabled?: boolean;
  labelComponent?: React.ComponentType;
  description?: string;
  component?: React.ElementType<InputBaseComponentProps>;
  fullWidth?: boolean;
  multiline?: boolean;
  rows?: number;
  error?: string | boolean;
  defaultValue?: string;
  value?: string | number;
  required?: boolean;
  autoFocus?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  inputProps?: InputBaseComponentProps;
  placeholder?: string;
  readOnly?: boolean;
  inputClassName?: string;
  inputEndAdornment?: React.ReactNode;
  inputStartAdornment?: React.ReactNode;
  onInputLabelClick?(event: React.MouseEvent): void;
  inputFocused?: boolean;
  onClick?: MouseEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  type?: "text" | "password" | "number";
  getRemainingCharsMessage?: (remainingChars: string) => string;
}

const useStyles = makeStyles<Theme, Props>(styles);

const Input = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    id,
    className,
    description,
    component = "input",
    size = "lg",
    fullWidth = false,
    multiline = false,
    rows,
    error = false,
    defaultValue,
    required = false,
    autoFocus = false,
    label,
    labelComponent: LabelComponent,
    inputProps,
    onChange,
    placeholder,
    value,
    readOnly,
    inputClassName = "",
    inputEndAdornment,
    inputStartAdornment,
    onInputLabelClick,
    inputFocused = false,
    disabled = false,
    getRemainingCharsMessage,
    ...rest
  } = props;

  const classes = useStyles(props);
  const currentLength = value ? `${value}`.length : 0;

  const rootClassName = classnames(
    {
      [classes.fullWidth]: fullWidth,
      [classes.error]: error,
      [classes.value]: value,
    },
    classes.root,
    classes[size],
    className,
  );

  return (
    <div className={classes.container}>
      <InputLabel htmlFor={id} onClick={onInputLabelClick}>
        <div className={classes.label}>
          {label && required ? `${label}*` : label}
          {LabelComponent && <LabelComponent />}
        </div>
        <InputBase
          id={id}
          ref={ref}
          classes={{
            root: classnames(rootClassName, {
              [classes.inputFocused]: inputFocused,
              [classes.disabled]: disabled,
            }),
            focused: classes.inputFocused,
            input: classnames(classes.input, {
              [classes.inputDisabled]: disabled,
              [inputClassName]: inputClassName,
            }),
          }}
          autoFocus={autoFocus}
          fullWidth={fullWidth}
          defaultValue={defaultValue}
          disabled={disabled}
          error={!!error}
          inputComponent={component}
          readOnly={readOnly}
          required={required}
          onChange={onChange}
          placeholder={placeholder}
          startAdornment={inputStartAdornment}
          endAdornment={inputEndAdornment}
          inputProps={inputProps}
          multiline={multiline}
          rows={rows}
          value={value}
          {...rest}
        />
      </InputLabel>
      {getRemainingCharsMessage && inputProps?.maxLength && (
        <FormHelperText className={classes.description}>
          {getRemainingCharsMessage?.(
            `${inputProps.maxLength - currentLength}`,
          )}
        </FormHelperText>
      )}
      <InputHelpText isError={!!error}>{error || description}</InputHelpText>
    </div>
  );
});

export default Input;
